import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI, User } from '../services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: () => void;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  fetchUser: () => Promise<void>;
  
  // Local authentication methods
  registerWithEmail: (data: { email: string; password: string; name: string }) => Promise<{ success: boolean; message?: string; error?: string; requiresVerification?: boolean }>;
  loginWithEmail: (data: { email: string; password: string }) => Promise<{ success: boolean; message?: string; user?: User; error?: string; requiresVerification?: boolean }>;
  verifyEmail: (token: string) => Promise<{ success: boolean; message?: string; error?: string; alreadyVerified?: boolean }>;
  resendVerification: (email: string) => Promise<{ success: boolean; message?: string; error?: string }>;
  forgotPassword: (email: string) => Promise<{ success: boolean; message?: string; error?: string }>;
  resetPassword: (data: { token: string; password: string }) => Promise<{ success: boolean; message?: string; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    console.log('🔍 Frontend: Checking auth...');
    try {
      const result = await authAPI.getCurrentUser();
      console.log('🔍 Frontend: Auth API response:', result);
      
      if (result.success && result.user) {
        console.log('✅ Frontend: User authenticated:', result.user.name);
        setUser(result.user);
      } else {
        console.log('❌ Frontend: Authentication failed:', result.error);
        setUser(null);
      }
    } catch (error) {
      console.error('❌ Frontend: Auth check error:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchUser = async () => {
    console.log('🔄 Frontend: Refreshing user data...');
    try {
      const result = await authAPI.getCurrentUser();
      
      if (result.success && result.user) {
        console.log('✅ Frontend: User data refreshed:', result.user.name);
        setUser(result.user);
      } else {
        console.log('❌ Frontend: Failed to refresh user data:', result.error);
        setUser(null);
      }
    } catch (error) {
      console.error('❌ Frontend: User refresh error:', error);
      setUser(null);
    }
  };

  // Google OAuth login
  const login = () => {
    authAPI.loginWithGoogle();
  };

  const logout = async () => {
    try {
      await authAPI.logout();
      setUser(null);
      // Redirect to home page
      window.location.href = '/';
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Local authentication methods
  const registerWithEmail = async (data: { email: string; password: string; name: string }) => {
    console.log('🔄 Frontend: Registering with email...');
    return await authAPI.registerWithEmail(data);
  };

  const loginWithEmail = async (data: { email: string; password: string }) => {
    console.log('🔄 Frontend: Logging in with email...');
    const result = await authAPI.loginWithEmail(data);
    
    if (result.success && result.user) {
      console.log('✅ Frontend: Local login successful:', result.user.name);
      setUser(result.user);
    }
    
    return result;
  };

  const verifyEmail = async (token: string) => {
    console.log('🔄 Frontend: Verifying email...');
    return await authAPI.verifyEmail(token);
  };

  const resendVerification = async (email: string) => {
    console.log('🔄 Frontend: Resending verification email...');
    return await authAPI.resendVerification(email);
  };

  const forgotPassword = async (email: string) => {
    console.log('🔄 Frontend: Requesting password reset...');
    return await authAPI.forgotPassword(email);
  };

  const resetPassword = async (data: { token: string; password: string }) => {
    console.log('🔄 Frontend: Resetting password...');
    return await authAPI.resetPassword(data);
  };

  useEffect(() => {
    console.log('🔍 Frontend: AuthProvider mounted, checking auth...');
    checkAuth();
  }, []);

  const value = {
    user,
    loading,
    login,
    logout,
    checkAuth,
    fetchUser,
    
    // Local authentication methods
    registerWithEmail,
    loginWithEmail,
    verifyEmail,
    resendVerification,
    forgotPassword,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 